import React, { useState, useEffect } from 'react';
import Sidebar from '../components/dashboard/Sidebar';
import { useRecruiters } from '../context/RecruiterContext';
import { useUser } from '../context/UserContext';
import { FiMail, FiTrash2, FiAlertCircle, FiCheck, FiX } from 'react-icons/fi';
import { motion } from 'framer-motion';

const RecruiterManagementPage = () => {
  const { 
    recruiters, 
    loading, 
    error, 
    availableSeats, 
    totalSeats, 
    inviteRecruiter, 
    removeRecruiter,
    fetchRecruiters,
    clearError
  } = useRecruiters();
  
  const { user } = useUser();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [inviteStatus, setInviteStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchRecruiters();
  }, [fetchRecruiters]);
  
  // Debug available seats
  useEffect(() => {
    console.log('Available seats:', availableSeats, 'Total seats:', totalSeats, 'Active/Pending:', recruiters.filter(r => r.status === 'active' || r.status === 'pending').length);
  }, [availableSeats, totalSeats, recruiters]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteStatus('sending');
    clearError(); // Clear any previous errors
    
    try {
      const result = await inviteRecruiter(email, name);
      
      if (result && (result === true || result.success === true)) {
        // Check if this was a re-invitation
        const wasReInvited = result.reInvited === true;
        
        // Set different status based on whether this was a re-invitation
        setInviteStatus(wasReInvited ? 'reInvited' : 'success');
        setEmail('');
        setName('');
        setTimeout(() => setInviteStatus(null), 5000);
      } else {
        setInviteStatus('error');
        // Don't auto-hide error messages
      }
    } catch (err) {
      console.error('Error in handleInvite:', err);
      setInviteStatus('error');
    }
  };

  const handleRemoveClick = (recruiter) => {

    setSelectedRecruiter(recruiter);
    setShowModal(true);
  };

  const confirmRemove = async () => {
    if (selectedRecruiter) {
      setDeleteLoading(true);
      try {

        const result = await removeRecruiter(selectedRecruiter.id);

        if (result) {
          // Success - close modal and clear selected recruiter
          setShowModal(false);
          setSelectedRecruiter(null);
          
          // Force refresh recruiters list
          await fetchRecruiters();
        } else {
          // If removeRecruiter returns false, keep modal open
          console.error('Failed to remove recruiter');
          alert('Failed to remove recruiter. Please try again.');
        }
      } catch (err) {
        console.error('Error removing recruiter:', err);
        alert('An error occurred while removing the recruiter. Please try again.');
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  return (
    <div className="flex bg-white min-h-screen font-sans">
      <Sidebar />
      <main className="flex-1 p-8 ml-0 md:ml-24">
        <header className="mb-8 animate-fade-in transition-all duration-300">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-black">{user?.name || 'Employer'}</h1>
            <div className="flex items-center gap-4">
              <button className="relative p-2 text-gray-600 hover:text-black transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-bell">
                  <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"></path>
                  <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path>
                </svg>
              </button>
              <div className="w-10 h-10 bg-white border border-gray-200 rounded-full overflow-hidden">
                <img src={user?.profileImage || "https://i.pravatar.cc/150?img=68"} alt={user?.name || 'User'} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </header>

        {/* Page Header with Black Background */}
        <div className="mt-8 bg-black p-6 border border-black rounded-lg" style={{ boxShadow: 'rgba(255, 255, 255, 0.8) 3px 3px 0px', transform: 'translateX(-3px) translateY(-3px)' }}>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white">Recruiter Management</h1>
              <p className="text-white mt-1">Invite and manage recruiters on your team</p>
            </div>
            <div className="bg-white text-black px-4 py-2 rounded-lg">
              <span className="font-bold">{recruiters.filter(r => r.status === 'active' || r.status === 'pending').length}</span> / <span className="font-bold">{totalSeats}</span> seats used
            </div>
          </div>
        </div>

        {/* Invite Recruiter Form */}
        <div className="mt-6 bg-white p-8 border border-black rounded-lg" style={{ boxShadow: 'rgba(0, 0, 0, 0.8) 3px 3px 0px', transform: 'translateX(-3px) translateY(-3px)' }}>
          <h2 className="text-xl font-bold text-black mb-6">Invite a New Recruiter</h2>
          
          {/* Only show warning when no seats are available - calculate this directly */}
          {(totalSeats - recruiters.filter(r => r.status === 'active' || r.status === 'pending').length) <= 0 && (
            <div className="bg-yellow-100 border-l-4 border-yellow-500 p-4 mb-6">
              <div className="flex items-center">
                <FiAlertCircle className="text-yellow-500 mr-2" />
                <p className="text-sm text-yellow-700">
                  You've used all available seats in your current plan. <a href="/pricing" className="font-bold underline">Upgrade your plan</a> to add more recruiters.
                </p>
              </div>
            </div>
          )}
          
          <form onSubmit={handleInvite} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-black mb-2">Recruiter Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 border border-black focus:outline-none transition-all bg-white"
                  placeholder="Enter recruiter name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-black mb-2">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-black focus:outline-none transition-all bg-white"
                  placeholder="Enter email address"
                  required
                />
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                type="submit"
                className={`flex items-center gap-2 px-6 py-3 ${(totalSeats - recruiters.filter(r => r.status === 'active' || r.status === 'pending').length) <= 0 ? 'bg-gray-300 cursor-not-allowed' : 'bg-black hover:bg-gray-800'} text-white font-semibold transition-colors`}
                disabled={inviteStatus === 'sending' || (totalSeats - recruiters.filter(r => r.status === 'active' || r.status === 'pending').length) <= 0}
              >
                {inviteStatus === 'sending' ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending Invitation...
                  </>
                ) : (
                  <>
                    <FiMail />
                    Send Invitation
                  </>
                )}
              </button>
            </div>
            
            {inviteStatus === 'success' && (
              <div className="bg-green-100 border-l-4 border-green-500 p-4 mt-4">
                <div className="flex items-center">
                  <FiCheck className="text-green-500 mr-2" />
                  <p className="text-sm text-green-700">Invitation sent successfully!</p>
                </div>
              </div>
            )}
            
            {inviteStatus === 'reInvited' && (
              <div className="bg-blue-100 border-l-4 border-blue-500 p-4 mt-4">
                <div className="flex items-center">
                  <FiCheck className="text-blue-500 mr-2" />
                  <p className="text-sm text-blue-700">Recruiter re-invited successfully! A previously removed recruiter has been re-activated.</p>
                </div>
              </div>
            )}
            
            {inviteStatus === 'error' && (
              <div className="bg-red-100 border-l-4 border-red-500 p-4 mt-4">
                <div className="flex items-center">
                  <FiX className="text-red-500 mr-2" />
                  <p className="text-sm text-red-700">
                    {error || 'Failed to send invitation. Please try again.'}
                    {process.env.NODE_ENV !== 'production' && (
                      <span className="block mt-1 text-xs text-gray-500">
                        Check browser console for more details.
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Recruiters List */}
        <div className="mt-8 bg-white p-8 border border-black rounded-lg" style={{ boxShadow: 'rgba(0, 0, 0, 0.8) 3px 3px 0px', transform: 'translateX(-3px) translateY(-3px)' }}>
          <h2 className="text-xl font-bold text-black mb-6">Your Recruiters</h2>
          
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <svg className="animate-spin h-8 w-8 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : recruiters.length === 0 ? (
            <div className="text-center py-12">
              <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <FiMail className="text-gray-500" size={24} />
              </div>
              <h3 className="text-lg font-medium text-gray-900">No recruiters yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Invite team members to help manage your hiring process
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Active
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Login Hours
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recruiters.map((recruiter) => (
                    <motion.tr 
                      key={recruiter.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="text-sm font-medium text-gray-900">{recruiter.name}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{recruiter.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          recruiter.status === 'active' ? 'bg-green-100 text-green-800' : 
                          recruiter.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {recruiter.status === 'active' ? 'Active' : 
                           recruiter.status === 'pending' ? 'Pending' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {recruiter.joinedAt ? new Date(recruiter.joinedAt).toLocaleDateString() : 'Not joined yet'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {recruiter.lastActiveAt ? new Date(recruiter.lastActiveAt).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex flex-col">
                          <span className="font-medium">{recruiter.loginHours?.today || 0} hrs today</span>
                          <span className="text-xs text-gray-400">{recruiter.loginHours?.thisWeek || 0} hrs this week</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleRemoveClick(recruiter)}
                          className="text-red-600 hover:text-red-900 flex items-center gap-1 ml-auto"
                        >
                          <FiTrash2 size={16} />
                          <span>Remove</span>
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-lg font-bold text-black mb-4">Remove Recruiter</h3>
            <p className="mb-6 text-gray-700">
              Are you sure you want to remove <span className="font-semibold">{selectedRecruiter?.name}</span>? 
              This will revoke their access to your employer dashboard.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowModal(false)}
                disabled={deleteLoading}
                className={`px-4 py-2 border border-gray-300 rounded-md ${deleteLoading ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                Cancel
              </button>
              <button
                onClick={confirmRemove}
                disabled={deleteLoading}
                className={`px-4 py-2 ${deleteLoading ? 'bg-red-400' : 'bg-red-600 hover:bg-red-700'} text-white rounded-md flex items-center justify-center min-w-[80px]`}
              >
                {deleteLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Removing...
                  </>
                ) : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecruiterManagementPage;
